import { useState } from 'react'

import AddIcon from '@mui/icons-material/Add'
import Box from '@mui/material/Box'

import { ToggleSetButton } from '@components/ToggleSetButton'
import type { Entity } from '@models/Entity'
import {
  canUserCreateSubprocesForProject,
  getPossibleAssigneesForProject,
  isUserVerantwoordelijkForProject,
} from '@util/project-util'

import type { Project } from '../../../aansluiting/project/types/Project'
import type { AuthenticatedUser } from '../../../auth/types/AuthenticatedUser'
import type { HoofdleidingProject } from '../../../hoofdleiding/project/types/HoofdleidingProject'
import type { BijlageTemplate } from '../../../templates/types/BijlageTemplate'
import { PageSection } from '../../../theme/components/PageSection'
import { StyledIconButton } from '../../../theme/components/StyledIconButton'
import { BijlagenDialog } from '../../bijlagen/components/BijlagenDialog'
import { useOpenBijlageForEntity } from '../../bijlagen/hooks/useOpenBijlageForEntity'
import type { AssignedBijlage } from '../../bijlagen/types/AssignedBijlage'
import type { Bijlage } from '../../bijlagen/types/Bijlage'
import type { BijlagenDialogType } from '../../bijlagen/types/BijlagenDialogType'
import type { Subproces } from '../types/Subproces'
import type { SubprocesDialogType } from '../types/SubprocesDialogType'
import type { SubprocesTemplate } from '../types/SubprocesTemplate'

import { SubprocesBodem } from './SubprocesBodem'
import { SubprocesDialog } from './SubprocesDialog'
import { SubprocesLog } from './SubprocesLog'
import { SubprocesNetcheck } from './SubprocesNetcheck'
import { SubprocesSchouw } from './SubprocesSchouw'

const notCancelledFilter = (x) => x.status !== 'cancelled'
const cancelledFilter = (x) => x.status === 'cancelled'

interface Props {
  project: Project | HoofdleidingProject
  subprocessen: Subproces[]
  entity: Entity
  aannemers?: string[]
  user: AuthenticatedUser
  templates: SubprocesTemplate[]
  bijlageTemplates: BijlageTemplate[]
  assignedBijlagen: AssignedBijlage[]
  bijlagen: Bijlage[]
}

const generateSubheading = (
  subprocessen: Subproces[],
  showCancelled: boolean,
) => {
  if (!subprocessen.length) {
    return 'Geen subprocessen'
  }

  if (showCancelled) {
    return 'Alle subprocessen'
  }

  return 'Actieve subprocessen'
}

export const Subprocessen = ({
  project,
  subprocessen,
  entity,
  aannemers,
  user,
  templates,
  bijlageTemplates,
  assignedBijlagen,
  bijlagen,
}: Props) => {
  const [showCancelled, setShowCancelled] = useState(false)
  const [logDrawer, setLogDrawer] = useState(false)
  const [type, setType] = useState<SubprocesDialogType | undefined>(undefined)
  const [selectedSubProces, setSelectedSubproces] = useState<
    Subproces | undefined
  >(undefined)
  const assignees = getPossibleAssigneesForProject({ project })
  const isVerantwoordelijke = isUserVerantwoordelijkForProject({
    user,
    project,
  })

  const handleClickSubproces =
    (subproces?: Subproces) => (type: SubprocesDialogType) => () => {
      setType(type)
      setSelectedSubproces(subproces)
    }

  const handleClickBijlage =
    (subproces?: Subproces) =>
    (type: BijlagenDialogType) =>
    (assignedBijlage?: AssignedBijlage | Bijlage) => {
      setSelectedSubproces(subproces)
      setBijlagenDialog({ open: !bijlagenDialog.open, type, assignedBijlage })
    }

  const handleClickOpenBijlage = (bijlage: Bijlage) => {
    openBijlageForEntity({
      entity,
      bijlage,
    })
  }

  const isAllowedToCreateSubproces = canUserCreateSubprocesForProject({
    project,
    user,
    templates,
  })
  const [bijlagenDialog, setBijlagenDialog] = useState<{
    open: boolean
    type: BijlagenDialogType
    assignedBijlage?: AssignedBijlage | Bijlage
  }>({
    open: false,
    type: 'create',
  })

  const { mutate: openBijlageForEntity } = useOpenBijlageForEntity()

  return (
    <>
      <PageSection
        id="subprocessen"
        heading="Subprocessen"
        subheading={generateSubheading(subprocessen, showCancelled)}
        actions={
          <>
            {isAllowedToCreateSubproces && (
              <StyledIconButton
                icon={<AddIcon />}
                title="Aanmaken subproces"
                onClick={handleClickSubproces()('create')}
              />
            )}
            <ToggleSetButton
              active={showCancelled}
              onClick={() => setShowCancelled(!showCancelled)}
              toggledSetSize={subprocessen.filter(cancelledFilter).length}
              type="subprocessen"
            />
          </>
        }
      >
        {subprocessen.length > 0 && (
          <Box>
            {subprocessen
              .filter(showCancelled ? () => true : notCancelledFilter)
              .map((subproces) => {
                const subprocesAssignedBijlagen = assignedBijlagen.filter(
                  (x) => x.subprocesId === subproces._id,
                )

                const subprocesBijlagen = bijlagen.filter((bijlage) => {
                  return subprocesAssignedBijlagen.some(
                    (assignedBijlage) =>
                      assignedBijlage._id === bijlage.parentId,
                  )
                })

                const clickBijlage = handleClickBijlage(subproces)
                const clickSubproces = handleClickSubproces(subproces)

                if (subproces.type === 'bodem') {
                  return (
                    <SubprocesBodem
                      key={`${subproces.type}-${subproces.changed}`}
                      subproces={subproces}
                      subprocessen={subprocessen}
                      assignedBijlagen={subprocesAssignedBijlagen}
                      bijlagen={subprocesBijlagen}
                      user={user}
                      isVerantwoordelijke={isVerantwoordelijke}
                      openLogDrawer={(subproces) => {
                        setSelectedSubproces(subproces)
                        setLogDrawer(true)
                      }}
                      onClickCancel={clickSubproces('cancel')}
                      onClickPause={clickSubproces('pause')}
                      onClickResume={clickSubproces('resume')}
                      onClickCreateBijlage={clickBijlage('create')}
                      onClickUploadBijlage={clickBijlage('upload')}
                      onClickCancelBijlage={clickBijlage('cancel')}
                      onClickOpleveren={clickBijlage('submit')}
                      onClickReviewBijlage={clickBijlage('review')}
                      onClickEditBijlage={clickBijlage('edit')}
                      onClickOpenBijlage={handleClickOpenBijlage}
                    />
                  )
                }

                if (subproces.type === 'netcheck') {
                  return (
                    <SubprocesNetcheck
                      project={project}
                      key={`${subproces.type}-${subproces.changed}`}
                      subproces={subproces}
                      subprocessen={subprocessen}
                      user={user}
                      openLogDrawer={(subproces) => {
                        setSelectedSubproces(subproces)
                        setLogDrawer(true)
                      }}
                      onClickCancel={clickSubproces('cancel')}
                      onClickPause={clickSubproces('pause')}
                      onClickResume={clickSubproces('resume')}
                      onClickHerplan={clickSubproces('herplan')}
                    />
                  )
                }

                return (
                  <SubprocesSchouw
                    key={`${subproces.type}-${subproces.changed}`}
                    subproces={subproces}
                    subprocessen={subprocessen}
                    openLogDrawer={(subproces) => {
                      setSelectedSubproces(subproces)
                      setLogDrawer(true)
                    }}
                    onClickPlan={clickSubproces('edit')}
                    onClickReview={clickSubproces('review')}
                    onClickCancel={clickSubproces('cancel')}
                    onClickHerplan={clickSubproces('herplan')}
                    onClickPause={clickSubproces('pause')}
                    onClickResume={clickSubproces('edit')}
                    onClickCreateBijlage={clickBijlage('create')}
                    onClickUploadBijlage={clickBijlage('upload')}
                    onClickCancelBijlage={clickBijlage('cancel')}
                    onClickOpleveren={clickBijlage('submit')}
                    onClickReviewBijlage={clickBijlage('review')}
                    onClickEditBijlage={clickBijlage('edit')}
                    onClickOpenBijlage={handleClickOpenBijlage}
                    user={user}
                    isVerantwoordelijke={isVerantwoordelijke}
                    assignedBijlagen={subprocesAssignedBijlagen}
                    bijlagen={subprocesBijlagen}
                  />
                )
              })}

            <SubprocesLog
              open={logDrawer}
              onClose={() => setLogDrawer(false)}
              subproces={selectedSubProces || subprocessen[0]}
            />
          </Box>
        )}
      </PageSection>

      <SubprocesDialog
        project={project}
        entity={entity}
        aannemers={aannemers}
        user={user}
        templates={templates}
        type={type}
        subproces={selectedSubProces}
        onClose={() => setType(undefined)}
      />

      {selectedSubProces && bijlagenDialog.assignedBijlage && (
        <BijlagenDialog
          type={bijlagenDialog.type}
          assignees={assignees}
          subproces={selectedSubProces}
          projectStartUitvoeringWeek={project.startUitvoeringWeek}
          processtatus={''} // processtatus moet hetzelfde zijn als bij de template - in toekomst kan dit mogelijk
          isHoofdleidingProject={project.type === 'Hoofdleiding'}
          onClose={() =>
            setBijlagenDialog({
              open: false,
              type: 'create',
              assignedBijlage: undefined,
            })
          }
          bijlageTemplates={bijlageTemplates.filter(
            (x) =>
              x.scope === 'subproces' && x.subproces === selectedSubProces.type,
          )}
          disciplineVerzamelingen={project.disciplineVerzamelingen}
          entity={entity}
          bijlage={bijlagenDialog.assignedBijlage}
        />
      )}
    </>
  )
}
